import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./theme.css"; /* after bootstrap so the theme tokens win the cascade */
import { useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { SUPPORTED_LANGS } from "./i18n";

import Home from "./Home";
import ForexMajors from "./ForexMajors";
import ForexMinors from "./ForexMinors";
import Metals from "./Metals";
import Indices from "./Indices";
import Cryptocurrencies from "./Cryptocurrencies";
import Commodities from "./Commodities";
import Articles from "./Articles";
import ArticlePage from "./ArticlePage";
import ContactUs from "./ContactUs";
import TermsOfUse from "./TermsOfUse";
import NotFound from "./NotFound";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LangWrapper from "./components/LangWrapper";

import EURUSD from "./predictions/forex-majors/EURUSD";
import GBPUSD from "./predictions/forex-majors/GBPUSD";
import USDJPY from "./predictions/forex-majors/USDJPY";
import USDCHF from "./predictions/forex-majors/USDCHF";
import USDCAD from "./predictions/forex-majors/USDCAD";
import AUDUSD from "./predictions/forex-majors/AUDUSD";
import NZDUSD from "./predictions/forex-majors/NZDUSD";
import AUDJPY from "./predictions/forex-minors/AUDJPY";
import AUDNZD from "./predictions/forex-minors/AUDNZD";
import CADCHF from "./predictions/forex-minors/CADCHF";
import EURCHF from "./predictions/forex-minors/EURCHF";
import AUDCAD from "./predictions/forex-minors/AUDCAD";
import GBPJPY from "./predictions/forex-minors/GBPJPY";
import GOLD from "./predictions/metals/GOLD";
import SILVER from "./predictions/metals/SILVER";
import COPPER from "./predictions/metals/COPPER";
import NAS100 from "./predictions/indices/NAS100";
import UK100 from "./predictions/indices/UK100";
import US30 from "./predictions/indices/US30";
import US500 from "./predictions/indices/US500";
import AU200 from "./predictions/indices/AU200";
import Bitcoin from "./predictions/cryptocurrencies/Bitcoin";
import BitcoinCash from "./predictions/cryptocurrencies/BitcoinCash";
import Ethereum from "./predictions/cryptocurrencies/Ethereum";
import Ripple from "./predictions/cryptocurrencies/Ripple";
import Litecoin from "./predictions/cryptocurrencies/Litecoin";
import Solana from "./predictions/cryptocurrencies/Solana";
import NaturalGas from "./predictions/commodities/NaturalGas";
import BrentCrudeOil from "./predictions/commodities/BrentCrudeOil";
import Cocoa from "./predictions/commodities/Cocoa";
import Sugar from "./predictions/commodities/Sugar";
import Corn from "./predictions/commodities/Corn";
import Soybean from "./predictions/commodities/Soybean";

function CookieRedirect() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const savedLang = Cookies.get("lang");
    if (!savedLang || !SUPPORTED_LANGS.includes(savedLang)) return;
    const alreadyLocalized = SUPPORTED_LANGS.some(
      (l) => pathname === `/${l}` || pathname.startsWith(`/${l}/`)
    );
    if (!alreadyLocalized) {
      const newPath = pathname === "/" ? `/${savedLang}` : `/${savedLang}${pathname}`;
      navigate(newPath, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function LanguageSyncer() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const langInPath = SUPPORTED_LANGS.find(
      (l) => pathname === `/${l}` || pathname.startsWith(`/${l}/`)
    );
    if (!langInPath) {
      i18n.changeLanguage("en");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      Cookies.remove("lang");
    }
  }, [pathname, i18n]);

  return null;
}

function App() {
  return (
    <div className="App">
      <Header />
      <CookieRedirect />
      <LanguageSyncer />
      <main>
        <Routes>
          {/* English routes — no prefix */}
          <Route path="/" element={<Home />} />
          <Route path="/forex-majors" element={<ForexMajors />} />
          <Route path="/forex-minors" element={<ForexMinors />} />
          <Route path="/metals" element={<Metals />} />
          <Route path="/indices" element={<Indices />} />
          <Route path="/cryptocurrencies" element={<Cryptocurrencies />} />
          <Route path="/commodities" element={<Commodities />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/:slug" element={<ArticlePage />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/forex-majors/eurusd-ai-trading-predictions" element={<EURUSD />} />
          <Route path="/forex-majors/gbpusd-ai-trading-predictions" element={<GBPUSD />} />
          <Route path="/forex-majors/usdjpy-ai-trading-predictions" element={<USDJPY />} />
          <Route path="/forex-majors/usdchf-ai-trading-predictions" element={<USDCHF />} />
          <Route path="/forex-majors/usdcad-ai-trading-predictions" element={<USDCAD />} />
          <Route path="/forex-majors/audusd-ai-trading-predictions" element={<AUDUSD />} />
          <Route path="/forex-majors/nzdusd-ai-trading-predictions" element={<NZDUSD />} />
          <Route path="/forex-minors/audjpy-ai-trading-predictions" element={<AUDJPY />} />
          <Route path="/forex-minors/audnzd-ai-trading-predictions" element={<AUDNZD />} />
          <Route path="/forex-minors/cadchf-ai-trading-predictions" element={<CADCHF />} />
          <Route path="/forex-minors/eurchf-ai-trading-predictions" element={<EURCHF />} />
          <Route path="/forex-minors/audcad-ai-trading-predictions" element={<AUDCAD />} />
          <Route path="/forex-minors/gbpjpy-ai-trading-predictions" element={<GBPJPY />} />
          <Route path="/metals/gold-ai-trading-predictions" element={<GOLD />} />
          <Route path="/metals/silver-ai-trading-predictions" element={<SILVER />} />
          <Route path="/metals/copper-ai-trading-predictions" element={<COPPER />} />
          <Route path="/indices/nas100-ai-trading-predictions" element={<NAS100 />} />
          <Route path="/indices/uk100-ai-trading-predictions" element={<UK100 />} />
          <Route path="/indices/us30-ai-trading-predictions" element={<US30 />} />
          <Route path="/indices/us500-ai-trading-predictions" element={<US500 />} />
          <Route path="/indices/au200-ai-trading-predictions" element={<AU200 />} />
          <Route path="/cryptocurrencies/bitcoin-ai-trading-predictions" element={<Bitcoin />} />
          <Route path="/cryptocurrencies/bitcoin-cash-ai-trading-predictions" element={<BitcoinCash />} />
          <Route path="/cryptocurrencies/ethereum-ai-trading-predictions" element={<Ethereum />} />
          <Route path="/cryptocurrencies/ripple-ai-trading-predictions" element={<Ripple />} />
          <Route path="/cryptocurrencies/litecoin-ai-trading-predictions" element={<Litecoin />} />
          <Route path="/cryptocurrencies/solana-ai-trading-predictions" element={<Solana />} />
          <Route path="/commodities/natural-gas-ai-trading-predictions" element={<NaturalGas />} />
          <Route path="/commodities/brent-crude-oil-ai-trading-predictions" element={<BrentCrudeOil />} />
          <Route path="/commodities/cocoa-ai-trading-predictions" element={<Cocoa />} />
          <Route path="/commodities/sugar-ai-trading-predictions" element={<Sugar />} />
          <Route path="/commodities/corn-ai-trading-predictions" element={<Corn />} />
          <Route path="/commodities/soybean-ai-trading-predictions" element={<Soybean />} />

          {/* Localised routes — /:lang prefix */}
          <Route path="/:lang" element={<LangWrapper />}>
            <Route index element={<Home />} />
            <Route path="forex-majors" element={<ForexMajors />} />
            <Route path="forex-minors" element={<ForexMinors />} />
            <Route path="metals" element={<Metals />} />
            <Route path="indices" element={<Indices />} />
            <Route path="cryptocurrencies" element={<Cryptocurrencies />} />
            <Route path="commodities" element={<Commodities />} />
            <Route path="articles" element={<Articles />} />
            <Route path="articles/:slug" element={<ArticlePage />} />
            <Route path="contact-us" element={<ContactUs />} />
            <Route path="terms-of-use" element={<TermsOfUse />} />
            <Route path="forex-majors/eurusd-ai-trading-predictions" element={<EURUSD />} />
            <Route path="forex-majors/gbpusd-ai-trading-predictions" element={<GBPUSD />} />
            <Route path="forex-majors/usdjpy-ai-trading-predictions" element={<USDJPY />} />
            <Route path="forex-majors/usdchf-ai-trading-predictions" element={<USDCHF />} />
            <Route path="forex-majors/usdcad-ai-trading-predictions" element={<USDCAD />} />
            <Route path="forex-majors/audusd-ai-trading-predictions" element={<AUDUSD />} />
            <Route path="forex-majors/nzdusd-ai-trading-predictions" element={<NZDUSD />} />
            <Route path="forex-minors/audjpy-ai-trading-predictions" element={<AUDJPY />} />
            <Route path="forex-minors/audnzd-ai-trading-predictions" element={<AUDNZD />} />
            <Route path="forex-minors/cadchf-ai-trading-predictions" element={<CADCHF />} />
            <Route path="forex-minors/eurchf-ai-trading-predictions" element={<EURCHF />} />
            <Route path="forex-minors/audcad-ai-trading-predictions" element={<AUDCAD />} />
            <Route path="forex-minors/gbpjpy-ai-trading-predictions" element={<GBPJPY />} />
            <Route path="metals/gold-ai-trading-predictions" element={<GOLD />} />
            <Route path="metals/silver-ai-trading-predictions" element={<SILVER />} />
            <Route path="metals/copper-ai-trading-predictions" element={<COPPER />} />
            <Route path="indices/nas100-ai-trading-predictions" element={<NAS100 />} />
            <Route path="indices/uk100-ai-trading-predictions" element={<UK100 />} />
            <Route path="indices/us30-ai-trading-predictions" element={<US30 />} />
            <Route path="indices/us500-ai-trading-predictions" element={<US500 />} />
            <Route path="indices/au200-ai-trading-predictions" element={<AU200 />} />
            <Route path="cryptocurrencies/bitcoin-ai-trading-predictions" element={<Bitcoin />} />
            <Route path="cryptocurrencies/bitcoin-cash-ai-trading-predictions" element={<BitcoinCash />} />
            <Route path="cryptocurrencies/ethereum-ai-trading-predictions" element={<Ethereum />} />
            <Route path="cryptocurrencies/ripple-ai-trading-predictions" element={<Ripple />} />
            <Route path="cryptocurrencies/litecoin-ai-trading-predictions" element={<Litecoin />} />
            <Route path="cryptocurrencies/solana-ai-trading-predictions" element={<Solana />} />
            <Route path="commodities/natural-gas-ai-trading-predictions" element={<NaturalGas />} />
            <Route path="commodities/brent-crude-oil-ai-trading-predictions" element={<BrentCrudeOil />} />
            <Route path="commodities/cocoa-ai-trading-predictions" element={<Cocoa />} />
            <Route path="commodities/sugar-ai-trading-predictions" element={<Sugar />} />
            <Route path="commodities/corn-ai-trading-predictions" element={<Corn />} />
            <Route path="commodities/soybean-ai-trading-predictions" element={<Soybean />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
